import React, { useEffect, useState } from "react";
import {
    Table,
    Tag,
    Card,
    Button,
    Space,
    Form,
    Input,
    Tooltip,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import useMessage from "antd/es/message/useMessage";
import { UserService } from "../../../../Services/Manage/UserService";
import { DepartmentEmpServices } from "../../../../Services/Manage/DepartmentEmpServices";
import CreateRoles from "./CreateRoles";

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const RolePage = () => {

    const perm = getPermission('/manage-employees');
    const [filteredData, setFilteredData] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [form] = Form.useForm();
    const [messageApi, messageContxt] = useMessage();

    const statusColors = {
        Inactive: "red",
        Active: "green",
    };

    const getAllLetterhead = async () => {
        try {
            const request = await DepartmentEmpServices.getAllRoles();
            setFilteredData(
                request.data.map((item, index) => ({
                    ...item,
                    si_no: index + 1,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getAllLetterhead();
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        if (value) {
            setFilteredData((prevData) =>
                prevData.filter((item) =>
                    Object.values(item).some(
                        (val) =>
                            val && typeof val === 'string' && val.toLowerCase().includes(value)
                    )
                )
            );
        } else {
            getAllLetterhead(); // Reset to original data when search is cleared
        }
    };

    const columns = [
        {
            title: "S. No.",
            dataIndex: "si_no",
            key: "si_no",
            align: 'center'
        },
        {
            title: "Roles",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag
                    style={{ fontSize: "16px", padding: "6px 12px" }}
                    color={statusColors[status]}>{status.toUpperCase()}</Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            fixed: 'right',
            render: (text, record) => (
                <Space size="middle">
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            size="large"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            disabled={!perm.edit}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.delete ? "You don't have permission to delete" : ""}>
                        <Button
                            size="large"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            disabled={!perm.delete}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const showDrawer = () => {
        setEditingItem(null);
        form.resetFields();
        setDrawerVisible(true);
    };

    const handleEdit = async (record) => {
        setEditingItem(record);
        const user = await UserService.getOmeUser();
        form.setFieldsValue({
            ...record,
            added_by: user.uid,
            status: record.status === "Active",
        });
        setDrawerVisible(true);
    };

    const handleDelete = async (record) => {
        try {
            const request = await DepartmentEmpServices.deleteRole(record.id);
            if (request.status) {
                messageApi.success(request.message);
                getAllLetterhead();
            } else {
                messageApi.error(request.message);
            }
        } catch (error) {
            console.log(error);
            messageApi.warning(error);
        }
    };

    const handleDrawerClose = () => {
        setDrawerVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        const user = await UserService.getOmeUser();
        const newItem = {
            ...values,
            added_by: user.uid,
            status: values.status ? "Active" : "Inactive",
        };

        if (editingItem) {
            const request = await DepartmentEmpServices.updateRoles(newItem, editingItem.id);
            if (request.status) {
                messageApi.success(request.message);
                getAllLetterhead();
            } else {
                messageApi.error(request.message);
            }
        } else {
            const request = await DepartmentEmpServices.createRole(newItem);
            if (request.status) {
                messageApi.success(request.message);
                getAllLetterhead();
            } else {
                messageApi.error(request.message);
            }
        }

        setDrawerVisible(false);
        form.resetFields();
    };

    return (
        <div>
            <Card
                title="Manage Roles"
                extra={
                    <Space>
                        <Input
                            size='large'
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                            style={{ width: 300 }}
                            allowClear
                        />
                       <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<PlusOutlined />}
                                onClick={showDrawer}
                                disabled={!perm.add}
                            >
                                Add Role
                            </Button>
                        </Tooltip>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: true }}
                />
            </Card>
            {messageContxt}

            <CreateRoles
                visible={drawerVisible}
                onClose={handleDrawerClose}
                onFinish={onFinish}
                form={form}
                editingItem={editingItem}
            />
        </div>
    );
};

export default RolePage;